<template>
  <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
    <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">{{ item.label }}</div>
    <a
      v-if="(!item.to || item.items) && item.visible !== false"
      :href="item.url"
      @click="itemClick($event, item, index)"
      :class="item.class"
      :target="item.target"
      tabindex="0"
      :aria-expanded="item.items ? isActiveMenu || expandAll : undefined"
    >
      <FontAwesomeIcon class="ml-2 text-xl" :icon="['fas', item.icon ?? 'house']" />
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
    </a>
    <router-link
      v-if="item.to && !item.items && item.visible !== false"
      @click="itemClick($event, item, index)"
      :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      :to="item.to"
      :aria-current="checkActiveRoute(item) ? 'page' : undefined"
    >
      <FontAwesomeIcon class="ml-2 text-xl" :icon="['fas', item.icon]" />
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
    </router-link>
    <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
      <ul v-show="root ? true : isActiveMenu || expandAll" class="layout-submenu">
        <app-menu-item
          v-for="(child, i) in item.items"
          :key="child"
          :index="i"
          :item="child"
          :parentItemKey="itemKey"
          :root="false"
          :expand-all="expandAll"
        ></app-menu-item>
      </ul>
    </Transition>
  </li>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLayout } from '@/layout/composables/layout';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const route = useRoute();
const router = useRouter();

const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } = useLayout();

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  index: {
    type: Number,
    default: 0
  },
  root: {
    type: Boolean,
    default: true
  },
  parentItemKey: {
    type: String,
    default: null
  },
  expandAll: {
    type: Boolean,
    default: false
  }
});

const itemKey = computed(() => (props.parentItemKey ? `${props.parentItemKey}-${props.index}` : String(props.index)));

const checkActiveRoute = (item) => {
  if (!item.to) return false;

  const target = router.resolve(item.to);
  const queryMatches = Object.entries(target.query).every(([key, value]) => String(route.query[key] ?? '') === String(value));

  return route.path === target.path && queryMatches;
};

const containsActiveRoute = (item) => item.items?.some((child) => checkActiveRoute(child) || containsActiveRoute(child)) ?? false;

const isActiveMenu = computed(() => {
  const activeKey = layoutConfig.activeMenuItem.value;
  const isManuallyActive = activeKey === itemKey.value || activeKey?.startsWith(`${itemKey.value}-`);

  return isManuallyActive || checkActiveRoute(props.item) || containsActiveRoute(props.item);
});
const itemClick = (event, item) => {
  if (item.disabled) {
    event.preventDefault();
    return;
  }

  const { overlayMenuActive, staticMenuMobileActive } = layoutState;

  if ((item.to || item.url) && (staticMenuMobileActive.value || overlayMenuActive.value)) {
    onMenuToggle();
  }

  if (item.command) {
    item.command({ originalEvent: event, item: item });
  }

  const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey) : itemKey.value;

  setActiveMenuItem(foundItemKey);
};

</script>

<style lang="scss" scoped></style>
